/**
 * @fileoverview Axios response interceptor.
 * @description Normalizes every rejected request into the shared ApiError shape, and handles the
 * identity backend's one session signal on a 401: `1020`. Its access-token guard answers every
 * failure — an expired access token included (they live 10 minutes by default) — with that same
 * code and no detail, so the first `1020` on a request triggers a silent refresh-and-retry,
 * deduplicated so concurrent 401s only trigger one POST /identity/token/refresh call; a `1020` on
 * the retried request means the session itself is gone, and force-logs-out. The refresh call carries no body at all — the refresh token
 * is an httpOnly cookie (named `ib_refresh` on the backend, though this app's JS never reads or
 * sends it explicitly — that's the point of httpOnly) the browser attaches on its own
 * (`withCredentials: true` on both this bare axios call and the shared httpClient). It still uses
 * a bare axios instance (not httpClient) so it never re-enters this same interceptor chain, but its
 * own success envelope still needs unwrapping manually (see performRefresh) for the same reason —
 * only httpClient's registered interceptor does that automatically. A failed refresh is not always
 * treated as a dead session: appCode `1017` (the refresh token/session itself is invalid) force-logs-
 * out, but `1018`/`1019` (rate-limited / the refresh service is briefly down) are transient — the
 * user's local session is still fine, so only the one request that triggered this refresh attempt
 * rejects with that transient error, without evicting them (see buildRefreshFailureException).
 * Retrying the original request needs httpClient itself, but this module can't import it
 * directly — http-client.ts wires this very function in as its response interceptor, so a static
 * import back would be a module dependency cycle. Instead, mirroring router-navigation.utils.ts's
 * setRouterNavigate pattern, http-client.ts registers itself here via setHttpClient once it's
 * created. A 403 with appCode `2008` (the caller's capabilities no longer allow that action — a
 * Super Admin changed them since this tab last loaded them) re-fetches the user's capabilities, so
 * the screen re-renders with the corrected menu/actions right after the failed click; it still
 * surfaces its inline message and no full-page redirect. A 403 with appCode `1043` (password change required — a restricted session) redirects
 * to /change-password. Also redirects to the full-page /403 or /500 screen on those statuses (via
 * router-navigation.utils, since this interceptor has no React Router context of its own — see
 * that file for why). Callers still get a rejected promise with a consistent error type, except a
 * successfully-retried request, which resolves normally as if the 401 never happened. Also unwraps
 * the identity backend's `{ httpStatus, message, traceId, data }` success envelope, when present,
 * so feature services keep working with just the inner `data` payload — detected structurally
 * rather than assumed for every response, since not every mocked endpoint has been migrated to
 * this envelope yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { apiConfig } from '@/config/api-config'
import { ApiException } from '@/shared/api/api.exception'
import { APP_ERROR_CODE_MAP } from '@/shared/constants/app-error-codes.constants'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { ROUTES } from '@/shared/constants/routes.constants'
import { refreshSessionAccess } from '@/shared/lib/auth/access-refresh.utils'
import { useAuthStore } from '@/shared/lib/auth/auth.store'
import { getStoredAccessToken } from '@/shared/lib/auth/token-storage.utils'
import { i18next } from '@/shared/lib/i18n/i18n.config'
import { navigateOutsideReact } from '@/shared/lib/navigation/router-navigation.utils'

import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'
import type { ApiEnvelope } from '@/shared/types/http.types'

interface ErrorResponseBody {
  appCode?: string
  message?: string
}

interface RefreshedSessionData {
  accessToken: string
  tokenType: 'Bearer'
  accessTokenExpiresAt: string
  sessionId: string
  sessionIdleExpiresAt: string | null
  sessionAbsoluteExpiresAt: string
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

// The identity backend's access-token guard answers every invalid, expired or revoked session
// with this one code, so it can't tell "expired, refresh me" from "gone": the first one is
// retried after a refresh, and one on the retried request ends the session.
const SESSION_INVALID_APP_CODE = '1020'
const REFRESH_SESSION_INVALID_APP_CODE = '1017'
// A restricted session (mustChangePassword): every route but GET /identity/me and Change Password
// answers 403 with this code until the password is changed.
const PASSWORD_CHANGE_REQUIRED_APP_CODE = '1043'
// The caller's capabilities don't allow this action (any more): the cue to re-fetch them.
const CAPABILITY_DENIED_APP_CODE = '2008'

// Every tab of this app shares one refresh cookie, and each refresh rotates it: two tabs refreshing
// with the same cookie look like token theft to the backend, which then revokes the session. This
// Web Lock (same name in every tab) makes refreshes take turns across tabs.
const REFRESH_LOCK_NAME = 'brownbox.auth.token-refresh'

let refreshPromise: Promise<string> | null = null
let httpClientRef: AxiosInstance | null = null

/**
 * @description Registers the shared Axios instance for this interceptor's own retry-with-new-token
 * call. Called once by http-client.ts right after creating it.
 * @param {AxiosInstance} client - The shared Axios instance.
 */
export function setHttpClient(client: AxiosInstance): void {
  httpClientRef = client
}

/**
 * @description Exchanges the httpOnly refresh-token cookie for a new access token and persists it,
 * without going through httpClient (to avoid re-entering this interceptor). No body to send and
 * nothing to check client-side first — the cookie is invisible to this app's JS, so a missing/
 * expired one is only discoverable by making the call and seeing it fail. Unwraps the response's
 * own success envelope manually, since this bare axios call doesn't go through httpClient's
 * registered interceptor (handleResponseSuccess) that normally does this. sessionId and the two
 * session-expiry timestamps are read but not stored, matching this codebase's existing stance on
 * them (see auth.service.ts/two-factor.service.ts) — nothing consumes them yet.
 * @returns {Promise<string>} The new access token.
 */
async function performRefresh(): Promise<string> {
  const { data } = await axios.post<ApiEnvelope<RefreshedSessionData>>(
    `${apiConfig.baseUrl}/identity/token/refresh`,
    undefined,
    { withCredentials: true },
  )

  useAuthStore.getState().setAccessToken(data.data.accessToken)

  return data.data.accessToken
}

/**
 * @description Refreshes while holding the cross-tab refresh lock. Once the lock is held, a stored
 * token different from the one the failed request carried means another tab refreshed while this
 * one waited — the access token lives in localStorage, shared by every tab — so that token is
 * reused instead of spending the (already rotated) refresh cookie again. Falls back to a plain
 * refresh where the Web Locks API isn't available.
 * @param {string | null} staleToken - The access token the failed request was sent with.
 * @returns {Promise<string>} The new access token.
 */
async function refreshAcrossTabs(staleToken: string | null): Promise<string> {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) {
    return performRefresh()
  }

  return navigator.locks.request(REFRESH_LOCK_NAME, async () => {
    const storedToken = getStoredAccessToken()

    if (storedToken && storedToken !== staleToken) {
      useAuthStore.getState().setAccessToken(storedToken)
      return storedToken
    }

    return performRefresh()
  })
}

/**
 * @description Returns the in-flight refresh call, starting one if none is running, so concurrent
 * 401s from several requests in this tab share a single refresh; refreshAcrossTabs then keeps
 * other tabs from refreshing at the same time.
 * @param {string | null} staleToken - The access token the failed request was sent with.
 * @returns {Promise<string>} The new access token.
 */
function refreshAccessToken(staleToken: string | null): Promise<string> {
  refreshPromise ??= refreshAcrossTabs(staleToken).finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

/**
 * @description Reads the bearer token a request was sent with.
 * @param {RetryableRequestConfig | undefined} request - The failed request's config.
 * @returns {string | null} The token, or null when the request carried none.
 */
function getRequestToken(request: RetryableRequestConfig | undefined): string | null {
  const header = request?.headers.get('Authorization')
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null
}

/**
 * @description Clears the local session and sends the visitor to /login, e.g. once a session is
 * confirmed revoked or a refresh attempt fails.
 */
function forceLogout(): void {
  useAuthStore.getState().logout()
  navigateOutsideReact(ROUTES.LOGIN)
}

/**
 * @description Retries the original request with a freshly refreshed access token, through
 * httpClient (not a bare axios call) so a failure on retry still gets normalized by this same
 * interceptor.
 * @param {string} accessToken - The freshly refreshed access token.
 * @param {RetryableRequestConfig} originalRequest - The request config to retry.
 * @returns {Promise<AxiosResponse>} The retried request's response.
 */
async function retryWithNewToken(
  accessToken: string,
  originalRequest: RetryableRequestConfig,
): Promise<AxiosResponse> {
  if (!httpClientRef) {
    throw new Error('httpClient has not registered itself with this interceptor yet.')
  }

  originalRequest.headers.set('Authorization', `Bearer ${accessToken}`)
  return httpClientRef.request(originalRequest)
}

/**
 * @description Builds the exception to reject with when a session has been confirmed unrecoverable
 * (the refresh call itself said so via appCode 1017, or failed in some other unrecognized way —
 * e.g. a network error), force-logging-out as a side effect.
 * @returns {ApiException} The exception to reject with.
 */
function buildSessionExpiredException(): ApiException {
  const mapped = APP_ERROR_CODE_MAP[REFRESH_SESSION_INVALID_APP_CODE]
  const message = mapped
    ? i18next.t(mapped.messageKey)
    : 'Your session has expired. Please sign in again.'

  forceLogout()
  return new ApiException(mapped?.code ?? ERROR_CODES.SESSION_EXPIRED, message, 401)
}

/**
 * @description Builds the exception a failed refresh call ultimately rejects with. A refresh that
 * fails because the session itself is no longer valid (appCode 1017, or anything unrecognized —
 * e.g. a network error) force-logs-out, since there's nothing left to recover. A refresh that
 * fails for a purely transient reason instead (1018 rate-limited, 1019 the refresh service itself
 * briefly unavailable) does not: the user's local session is still perfectly valid, so wiping it
 * over a blip would be wrong — only the one request that triggered this refresh attempt rejects
 * with that transient error.
 * @param {unknown} refreshError - The error the bare refresh call rejected with.
 * @returns {ApiException} The exception to reject the original request with.
 */
function buildRefreshFailureException(refreshError: unknown): ApiException {
  if (axios.isAxiosError(refreshError)) {
    const appCode = (refreshError.response?.data as ErrorResponseBody | undefined)?.appCode
    const mapped =
      appCode && appCode !== REFRESH_SESSION_INVALID_APP_CODE
        ? APP_ERROR_CODE_MAP[appCode]
        : undefined

    if (mapped) {
      return new ApiException(
        mapped.code,
        i18next.t(mapped.messageKey),
        refreshError.response?.status,
      )
    }
  }

  return buildSessionExpiredException()
}

/**
 * @description Handles an expired-access-token 401: refreshes and retries the original request. A
 * refresh failure is classified via buildRefreshFailureException (transient vs. session-dead); a
 * failure of the *retried* request itself is a separate concern and left to propagate its own
 * already-normalized exception (it re-enters this same interceptor through httpClient), rather
 * than being smothered into a generic session-expired message.
 * @param {RetryableRequestConfig} [originalRequest] - The request that failed, when Axios provided
 * one.
 * @returns {Promise<AxiosResponse>} The retried request's response.
 */
async function handleExpiredAccessToken(
  originalRequest: RetryableRequestConfig | undefined,
): Promise<AxiosResponse> {
  if (originalRequest) {
    originalRequest._retry = true
  }

  let newAccessToken: string

  try {
    newAccessToken = await refreshAccessToken(getRequestToken(originalRequest))
  } catch (refreshError) {
    throw buildRefreshFailureException(refreshError)
  }

  if (!originalRequest) {
    throw buildSessionExpiredException()
  }

  return retryWithNewToken(newAccessToken, originalRequest)
}

/**
 * @description Redirects to the full-page /403 or /500 screen for a status that means the
 * current page can't recover on its own — but only when the appCode isn't one this interceptor
 * already knows how to surface inline (see APP_ERROR_CODE_MAP). Several business-rule failures
 * legitimately reuse 403/5xx for a reason unrelated to "you can't access this page" — e.g. MFA's
 * max-attempts-exceeded is a 403, and login/MFA's "temporarily unavailable" scenarios are a
 * 503 — and those need their inline message shown on the current form, not a full-page bounce.
 * @param {number} status - The HTTP status code from the failed response.
 * @param {string} [appCode] - The backend's application error code, when present.
 */
function redirectOnFatalStatus(status: number, appCode: string | undefined): void {
  if (appCode !== undefined && appCode in APP_ERROR_CODE_MAP) {
    return
  }

  if (status === 403) {
    navigateOutsideReact(ROUTES.FORBIDDEN)
  } else if (status >= 500) {
    navigateOutsideReact(ROUTES.SERVER_ERROR)
  }
}

/**
 * @description Re-fetches the user's capabilities after a `403 / 2008`, unless the failed request
 * is one of the reads that re-fetch is made of (which would loop). Those reads say so themselves
 * with `skipAccessRefresh` on their request, so the guard doesn't depend on their URL (a trailing
 * slash, a prefix or a renamed path can't defeat it).
 * @param {number} status - The HTTP status code from the failed response.
 * @param {string} [appCode] - The backend's application error code, when present.
 * @param {RetryableRequestConfig} [request] - The failed request's config, when Axios provided one.
 */
function refreshAccessOnCapabilityDenied(
  status: number,
  appCode: string | undefined,
  request: RetryableRequestConfig | undefined,
): void {
  if (
    status === 403 &&
    appCode === CAPABILITY_DENIED_APP_CODE &&
    request?.skipAccessRefresh !== true
  ) {
    void refreshSessionAccess()
  }
}

/**
 * @description Whether a 401 may just be an expired access token (`1020`, on a request this
 * interceptor hasn't already retried once) and is worth a silent refresh-and-retry.
 * @param {number} status - The HTTP status code from the failed response.
 * @param {string} [appCode] - The backend's application error code, when present.
 * @param {boolean} alreadyRetried - Whether this request has already gone through one retry.
 * @returns {boolean} True when a silent refresh-and-retry should be attempted.
 */
function isExpiredAccessToken401(
  status: number,
  appCode: string | undefined,
  alreadyRetried: boolean,
): boolean {
  return status === 401 && appCode === SESSION_INVALID_APP_CODE && !alreadyRetried
}

/**
 * @description Whether a 401 means the session is gone for good: a `1020` that persists even on
 * the request already retried with a freshly refreshed access token.
 * @param {number} status - The HTTP status code from the failed response.
 * @param {string} [appCode] - The backend's application error code, when present.
 * @param {boolean} alreadyRetried - Whether this request has already gone through one retry.
 * @returns {boolean} True when the session should be force-logged-out.
 */
function isRevokedSession401(
  status: number,
  appCode: string | undefined,
  alreadyRetried: boolean,
): boolean {
  return status === 401 && appCode === SESSION_INVALID_APP_CODE && alreadyRetried
}

/**
 * @description Builds the ApiException a failed response ultimately rejects with, once the
 * refresh/revocation branches above don't apply. When the backend's appCode is a known one, the
 * generic localized message for that appCode wins over the raw backend message, so the UI never
 * leaks which internal failure reason (unknown identifier vs. wrong password vs. locked account,
 * ...) actually occurred.
 * @param {number} status - The HTTP status code from the failed response.
 * @param {ErrorResponseBody} [data] - The parsed response body, when present.
 * @returns {ApiException} The exception to reject with.
 */
function buildFailureException(status: number, data: ErrorResponseBody | undefined): ApiException {
  const mapped = data?.appCode ? APP_ERROR_CODE_MAP[data.appCode] : undefined

  if (mapped) {
    return new ApiException(mapped.code, i18next.t(mapped.messageKey), status)
  }

  const responseMessage = typeof data?.message === 'string' ? data.message : undefined

  return new ApiException(
    status === 401 ? ERROR_CODES.AUTH_INVALID_CREDENTIALS : ERROR_CODES.UNKNOWN_ERROR,
    responseMessage ?? 'Something went wrong. Please try again.',
    status,
  )
}

/**
 * @description Checks whether a response body is the identity backend's success envelope, rather
 * than assuming every response is enveloped — several mocked endpoints haven't been migrated to
 * it yet and still resolve with a flat body.
 * @param {unknown} body - The raw response body to check.
 * @returns {boolean} True when the body matches the `{ httpStatus, message, traceId, data }` shape.
 */
function isApiEnvelope(body: unknown): body is ApiEnvelope<unknown> {
  return (
    typeof body === 'object' &&
    body !== null &&
    'httpStatus' in body &&
    'traceId' in body &&
    'data' in body
  )
}

/**
 * @description Passes a successful response through, unwrapping the backend's success envelope
 * into just its `data` payload when present. A paginated list's envelope also carries `meta`
 * beside `data`; that is kept on the response as `pageMeta` (read it with toPaginated, in
 * pagination.utils.ts) so unwrapping doesn't lose it.
 * @param {AxiosResponse} response - The successful Axios response.
 * @returns {AxiosResponse} The same response, with an enveloped body replaced by its `data`.
 */
export function handleResponseSuccess(response: AxiosResponse): AxiosResponse {
  if (isApiEnvelope(response.data)) {
    const { data, meta } = response.data

    if (meta) {
      ;(response as ResponseWithPageMeta).pageMeta = meta
    }

    response.data = data
  }

  return response
}

/**
 * @description Converts a rejected request into an ApiException, except an expired-access-token
 * 401 that's successfully recovered by a silent refresh, which resolves normally instead.
 * @param {unknown} error - The error thrown by Axios.
 * @returns {Promise<AxiosResponse>} The retried request's response, when a silent refresh recovers
 * it.
 */
export async function handleResponseError(error: unknown): Promise<AxiosResponse> {
  if (!axios.isAxiosError(error)) {
    throw new ApiException(ERROR_CODES.UNKNOWN_ERROR, 'Something went wrong. Please try again.')
  }

  if (!error.response) {
    throw new ApiException(
      ERROR_CODES.NETWORK_ERROR,
      'Unable to reach the server. Check your connection and try again.',
    )
  }

  const { status } = error.response
  const data = error.response.data as ErrorResponseBody | undefined
  const appCode = data?.appCode
  const originalRequest = error.config as RetryableRequestConfig | undefined

  if (isExpiredAccessToken401(status, appCode, originalRequest?._retry === true)) {
    return handleExpiredAccessToken(originalRequest)
  }

  if (isRevokedSession401(status, appCode, originalRequest?._retry === true)) {
    forceLogout()
  }

  if (status === 403 && appCode === PASSWORD_CHANGE_REQUIRED_APP_CODE) {
    navigateOutsideReact(ROUTES.CHANGE_PASSWORD)
  }

  refreshAccessOnCapabilityDenied(status, appCode, originalRequest)
  redirectOnFatalStatus(status, appCode)

  throw buildFailureException(status, data)
}
